import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Loader2 } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { createInterfaceData, editInterfaceData, editIntExtInterfaceData } from "@/hooks/useLocalStorage";
import { InterfacePayload, InterfaceAction } from "@/lib/api-services";
import { AlertDialog } from "../../widgets/AlertDailog";


const EMPTY_FORM: InterfacePayload = {
    type: 0,
    name: "",
    input: "",
    output: "",
    description: "",
    implementStatus: false,
    repositoryInterfaceId: null
};

interface AddInterfaceDataProps {
    open: boolean;
    onClose: () => void;
    editData?: InterfaceAction | null;
}

export function AddInterfaceData({ open, onClose, editData }: AddInterfaceDataProps) {
    const [form, setForm] = useState<InterfacePayload>(EMPTY_FORM);
    const [alert, setAlert] = useState<{ open: boolean; message: string; status: boolean }>({
        open: false,
        message: "",
        status: false,
    });

    const createMutation = createInterfaceData();
    const editMutation = editInterfaceData();
    const editIntExpMutation = editIntExtInterfaceData();
    const isEditMode = !!editData;
    const isExternalEditMode = !!editData?.repositoryInterfaceId;

    useEffect(() => {
        if (editData && open) {
            setForm({
                type: editData.type,
                name: editData.name,
                input: editData.input,
                output: editData.output,
                description: editData.description || "",
                implementStatus: editData.implementStatus,
                repositoryInterfaceId: editData.repositoryInterfaceId
            });
        } else if (!open) {
            setForm(EMPTY_FORM);
        }
    }, [editData, open]);

    const handleChange = (field: keyof InterfacePayload, value: boolean | string | number) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const isFormValid = () => {
        if (isExternalEditMode) {
            return form.name.trim() !== "" &&
                form.description.trim() !== "";
        }

        return form.name.trim() !== "" &&
            form.input.trim() !== "" &&
            form.output.trim() !== "" &&
            form.description.trim() !== "";
    };

    const handleSave = () => {
        if (!isFormValid()) return;

        const payload = form;

        if (isExternalEditMode && editData) {
            const inTextPayload = {
                description: form.description,
                name: form.name,
                repositoryInterfaceId: form.repositoryInterfaceId
            }
            editIntExpMutation.mutate(
                { Id: editData.Id, data: inTextPayload },
                {
                    onSuccess: () => {
                        setAlert({
                            open: true,
                            message: `${editData.type === 1 ? "External" : "Internal"} Interface Updated Successfully!`,
                            status: true
                        });
                    },
                    onError: () => {
                        setAlert({
                            open: true,
                            message: `Failed to Update ${editData.type === 1 ? "External" : "Internal"} Interface.`,
                            status: false
                        });
                    },
                }
            );
            return;
        }

        if (isEditMode && editData) {
            editMutation.mutate(
                { Id: editData.Id, data: payload },
                {
                    onSuccess: () => {
                        setAlert({
                            open: true,
                            message: "Interface updated successfully!",
                            status: true
                        });
                    },
                    onError: () => {
                        setAlert({
                            open: true,
                            message: "Failed to update interface. Please try again.",
                            status: false
                        });
                    },
                }
            );
        } else {
            createMutation.mutate(payload, {
                onSuccess: () => {
                    setAlert({
                        open: true,
                        message: "Interface created successfully!",
                        status: true
                    });
                },
                onError: () => {
                    setAlert({
                        open: true,
                        message: "Failed to create interface. Please try again.",
                        status: false
                    });
                },
            });
        }
    };

    useEffect(() => {
        if (editIntExpMutation.isSuccess === true) {
            handleClose()
            editIntExpMutation.reset();
        } else if (editIntExpMutation.isError) {
            handleClose()
            editIntExpMutation.reset();
        }
    }, [editIntExpMutation]);

    useEffect(() => {
        if (createMutation.isSuccess === true) {
            handleClose()
            createMutation.reset();
        } else if (createMutation.isError) {
            handleClose()
            createMutation.reset();
        }
    }, [createMutation]);

    useEffect(() => {
        if (editMutation.isSuccess === true) {
            handleClose()
            editMutation.reset();
        } else if (editMutation.isError) {
            handleClose()
            editMutation.reset();
        }
    }, [editMutation]);

    const handleAlertClose = (value: boolean) => {
        setAlert((prev) => ({ ...prev, open: value }));
        if (alert.status) {
            setForm(EMPTY_FORM);
            onClose();
        }
    };

    const handleClose = () => {
        if (createMutation.isPending || editMutation.isPending) return;
        setForm(EMPTY_FORM);
        onClose();
    };

    const isPending = createMutation.isPending || editMutation.isPending || editIntExpMutation.isPending

    return (
        <>
            <Dialog open={open} onOpenChange={handleClose}>
                <DialogContent className="sm:max-w-lg flex flex-col max-h-[90vh] sm:max-h-[85vh]">
                    <DialogHeader className="flex-shrink-0">
                        <DialogTitle className="text-lg font-semibold">
                            {isEditMode ? "Edit Interface" : "Create Interface"}
                        </DialogTitle>
                    </DialogHeader>

                    <div className="flex-1 overflow-y-auto px-1 space-y-4 py-2">
                        {!isExternalEditMode && (
                            <div className="space-y-1.5">
                                <Label htmlFor="type">
                                    Type <span className="text-red-500">*</span>
                                </Label>

                                <Select
                                    value={form.type?.toString()} // number → string
                                    onValueChange={(val) => handleChange("type", Number(val))}
                                    disabled={isPending}
                                >
                                    <SelectTrigger id="type">
                                        <SelectValue placeholder="Select type" />
                                    </SelectTrigger>

                                    <SelectContent>
                                        <SelectItem value="0">Internal</SelectItem>
                                        <SelectItem value="1">External</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        )}

                        <div className="space-y-1.5">
                            <Label htmlFor="name">
                                Name <span className="text-red-500">*</span>
                            </Label>
                            <Input
                                id="name"
                                placeholder="Enter name"
                                value={form.name}
                                onChange={(e) => handleChange("name", e.target.value)}
                                disabled={isPending}
                            />
                        </div>

                        {!isExternalEditMode && (
                            <div className="space-y-1.5">
                                <Label htmlFor="input">
                                    Input <span className="text-red-500">*</span>
                                </Label>
                                <Input
                                    id="input"
                                    placeholder="Enter input"
                                    value={form.input}
                                    onChange={(e) => handleChange("input", e.target.value)}
                                    disabled={isPending}
                                />
                            </div>
                        )}

                        {!isExternalEditMode && (
                            <div className="space-y-1.5">
                                <Label htmlFor="output">
                                    Output <span className="text-red-500">*</span>
                                </Label>
                                <Input
                                    id="output"
                                    placeholder="Enter output"
                                    value={form.output}
                                    onChange={(e) => handleChange("output", e.target.value)}
                                    disabled={isPending}
                                />
                            </div>
                        )}

                        {!isExternalEditMode && (
                            <div className="space-y-1.5">
                                <div className="space-y-1.5">
                                    <Label>
                                        Implement Status <span className="text-red-500">*</span>
                                    </Label>

                                    <div className="flex items-center gap-6">
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="radio"
                                                id="impl-yes"
                                                name="implementStatus"
                                                checked={form.implementStatus === true}
                                                onChange={() => handleChange("implementStatus", true)}
                                                disabled={isPending}
                                            />
                                            <Label htmlFor="impl-yes" className="font-normal cursor-pointer">
                                                Yes
                                            </Label>
                                        </div>

                                        <div className="flex items-center gap-2">
                                            <input
                                                type="radio"
                                                id="impl-no"
                                                name="implementStatus"
                                                checked={form.implementStatus === false}
                                                onChange={() => handleChange("implementStatus", false)}
                                                disabled={isPending}
                                            />
                                            <Label htmlFor="impl-no" className="font-normal cursor-pointer">
                                                No
                                            </Label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="space-y-1.5">
                            <Label htmlFor="description">
                                Description <span className="text-red-500">*</span>
                            </Label>
                            <Textarea
                                id="description"
                                placeholder="Enter description"
                                value={form.description}
                                onChange={(e) => handleChange("description", e.target.value)}
                                disabled={isPending}
                                rows={3}
                            />
                        </div>

                        {isExternalEditMode && (
                            <div className="space-y-1.5">
                                <Label htmlFor="repositoryInterfaceId">
                                    Repository Interface Id <span className="text-red-500">*</span>
                                </Label>
                                <Input
                                    id="repositoryInterfaceId"
                                    placeholder="Enter repository interface id"
                                    value={(form as any).repositoryInterfaceId || ""}
                                    onChange={(e) =>
                                        handleChange("repositoryInterfaceId" as any, e.target.value)
                                    }
                                    disabled={true}
                                />
                            </div>
                        )}
                    </div>

                    <div className="flex-shrink-0 flex justify-end gap-2 pt-3 border-t">
                        <Button
                            variant="outline"
                            onClick={handleClose}
                            disabled={isPending}
                        >
                            Cancel
                        </Button>
                        <Button
                            className="bg-workfinity-500 hover:bg-workfinity-600 min-w-[80px]"
                            onClick={handleSave}
                            disabled={isPending || !isFormValid()}
                        >
                            {isPending ? (
                                <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    {isEditMode ? "Updating..." : "Saving..."}
                                </>
                            ) : (
                                isEditMode ? "Update" : "Save"
                            )}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            <AlertDialog
                open={alert.open}
                onOpenChange={handleAlertClose}
                message={alert.message}
                status={alert.status}
            />
        </>
    );
}